import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export interface ActivityFeedItem {
  id: string;
  business_id: string;
  event_type: string;
  entity_type: string;
  entity_id: string | null;
  title: string;
  description: string | null;
  actor_name: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const FEED_LIMIT = 50;

/**
 * Live Control Centre activity feed. Loads the latest rows once, then streams new
 * inserts via Supabase realtime. The backend precomputes all display fields, so
 * this hook does no joins or business logic — just fetch + subscribe.
 */
export function useActivityFeed() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setItems([]);
      seenIdsRef.current = new Set();
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('activity_feed')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(FEED_LIMIT);
      if (queryError) throw queryError;
      const rows = (data ?? []) as ActivityFeedItem[];
      seenIdsRef.current = new Set(rows.map((r) => r.id));
      setItems(rows);
    } catch (err) {
      console.error('Error loading activity feed:', err);
      setError('Could not load activity.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Realtime: prepend new inserts for this business.
  useEffect(() => {
    if (!supabase || !businessId) return;

    const channel = supabase
      .channel(`activity_feed_${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_feed',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const row = payload.new as ActivityFeedItem;
          if (!row?.id || seenIdsRef.current.has(row.id)) return;
          seenIdsRef.current.add(row.id);
          setItems((prev) => [row, ...prev].slice(0, FEED_LIMIT));
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [supabase, businessId]);

  return { items, loading, error, refresh: load };
}
